import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Leaf, 
  CheckCircle, 
  Users, 
  TrendingUp, 
  Shield, 
  ArrowRight,
  Star,
  Globe,
  Award,
  Target,
  Zap,
  BarChart3,
  Clock,
  TreePine,
  Factory,
  Wind,
  Sun,
  Play,
  Quote
} from 'lucide-react';

const HomePage = () => {
  const navigate = useNavigate();
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  const mainFeatures = [
    {
      title: 'Discover Projects',
      description: 'Browse verified carbon credit projects from renewable energy to forest conservation',
      icon: Globe,
      color: 'emerald',
      path: '/marketplace',
      stats: '500+ Projects',
      gradient: 'from-emerald-500 to-green-600'
    },
    {
      title: 'Smart Dashboard',
      description: 'Manage your entire carbon credit portfolio with AI-powered insights',
      icon: BarChart3,
      color: 'blue',
      path: '/dashboard',
      stats: 'AI-Powered',
      gradient: 'from-blue-500 to-indigo-600'
    },
    {
      title: 'Expert Verification',
      description: 'Connect with certified verifiers and ensure project authenticity',
      icon: Shield,
      color: 'purple',
      path: '/verify',
      stats: '50+ Experts',
      gradient: 'from-purple-500 to-pink-600'
    },
    {
      title: 'Impact Tracking',
      description: 'Monitor real-world environmental impact with transparent reporting',
      icon: TrendingUp,
      color: 'orange',
      path: '/about',
      stats: 'Real-Time',
      gradient: 'from-orange-500 to-red-600'
    }
  ];

  const projectTypes = [
    { icon: TreePine, name: 'Forestry', count: '150+', color: 'green' },
    { icon: Wind, name: 'Wind Energy', count: '120+', color: 'blue' },
    { icon: Sun, name: 'Solar Power', count: '180+', color: 'yellow' },
    { icon: Factory, name: 'Industrial', count: '50+', color: 'gray' }
  ];

  const stats = [
    { label: 'CO₂ Reduced', value: '2.5M', unit: 'tons', icon: Leaf, color: 'green' },
    { label: 'Active Projects', value: '500', unit: '+', icon: Target, color: 'blue' },
    { label: 'Expert Verifiers', value: '50', unit: '+', icon: Award, color: 'purple' },
    { label: 'Countries', value: '25', unit: '+', icon: Globe, color: 'orange' }
  ];

  const testimonials = [
    {
      name: 'Sarah Chen',
      role: 'Sustainability Director',
      company: 'GreenTech Solutions',
      content: 'Climify transformed how we manage our carbon credit portfolio. The verification process is transparent and the marketplace is incredibly user-friendly.',
      rating: 5,
      avatar: '👩‍💼'
    },
    {
      name: 'Marcus Rodriguez',
      role: 'Environmental Consultant',
      company: 'EcoConsult Inc.',
      content: 'As a verifier, I appreciate the professional tools and streamlined workflow. The multi-verifier system builds real trust in the market.',
      rating: 5,
      avatar: '👨‍🔬'
    },
    {
      name: 'Dr. Aisha Patel',
      role: 'Climate Researcher',
      company: 'Carbon Research Institute',
      content: 'The platform\'s commitment to transparency and scientific rigor is exactly what the carbon credit market needs. Outstanding work!',
      rating: 5,
      avatar: '👩‍🔬'
    }
  ];

  const verificationHighlights = [
    { icon: Shield, text: 'Multi-verifier support for enhanced credibility', color: 'blue' },
    { icon: Award, text: 'Industry-standard verification protocols', color: 'purple' },
    { icon: Users, text: 'Network of qualified verification experts', color: 'green' },
    { icon: Star, text: 'Transparent credibility scoring system', color: 'yellow' }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-green-50 via-blue-50 to-purple-50 overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16 relative">
          <div className="text-center">
            <div className="inline-flex items-center bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Zap className="h-4 w-4 mr-2" />
              Leading Carbon Credit Platform
            </div>
            
            <h1 className="text-5xl md:text-7xl font-bold text-gray-900 mb-6">
              Carbon Credits
              <span className="bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent block">
                Simplified
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-4xl mx-auto leading-relaxed">
              The world's most trusted platform for carbon credit creation, verification, and trading. 
              <span className="text-green-600 font-semibold"> Join the climate action revolution.</span>
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <button 
                onClick={() => navigate('/marketplace')}
                className="group bg-gradient-to-r from-green-600 to-emerald-600 text-white px-8 py-4 rounded-xl text-lg font-semibold hover:from-green-700 hover:to-emerald-700 transition-all duration-300 flex items-center justify-center shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              >
                Explore Marketplace
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <button 
                onClick={() => navigate('/auth?mode=register')}
                className="group border-2 border-green-600 text-green-600 px-8 py-4 rounded-xl text-lg font-semibold hover:bg-green-600 hover:text-white transition-all duration-300 flex items-center justify-center"
              >
                <Play className="mr-2 h-5 w-5" />
                Watch Demo
              </button>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <div key={index} className="text-center group">
                  <div className={`bg-white rounded-2xl w-16 h-16 flex items-center justify-center mx-auto mb-4 shadow-lg group-hover:shadow-xl transition-shadow border-2 border-${stat.color}-100`}>
                    <stat.icon className={`h-8 w-8 text-${stat.color}-600`} />
                  </div>
                  <div className="text-3xl font-bold text-gray-900 mb-1">
                    {stat.value}<span className="text-lg">{stat.unit}</span>
                  </div>
                  <div className="text-gray-600 font-medium">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Project Types Section */}
      <div className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Project Categories</h2>
            <p className="text-lg text-gray-600">Supporting diverse environmental initiatives worldwide</p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {projectTypes.map((type, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow text-center group cursor-pointer"
                onClick={() => navigate('/marketplace')}
              >
                <div className={`w-16 h-16 bg-${type.color}-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform`}>
                  <type.icon className={`h-8 w-8 text-${type.color}-600`} />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">{type.name}</h3>
                <p className="text-gray-600 text-sm">{type.count} projects</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main Features */}
      <div className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Everything You Need</h2>
            <p className="text-xl text-gray-600">Comprehensive tools for the entire carbon credit lifecycle</p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {mainFeatures.map((feature, index) => (
              <div
                key={index}
                onClick={() => navigate(feature.path)}
                className="group relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer overflow-hidden border border-gray-100"
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${feature.gradient} opacity-0 group-hover:opacity-5 transition-opacity`}></div>
                <div className="p-8 relative">
                  <div className="flex items-start justify-between mb-6">
                    <div className={`w-16 h-16 bg-gradient-to-br ${feature.gradient} rounded-xl flex items-center justify-center shadow-lg`}>
                      <feature.icon className="h-8 w-8 text-white" />
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                        {feature.stats}
                      </span>
                      <ArrowRight className="h-5 w-5 text-gray-400 group-hover:text-green-600 group-hover:translate-x-1 transition-all" />
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-green-600 transition-colors">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed text-lg">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Verification Trust Section */}
      <div className="py-16 bg-gradient-to-r from-blue-600 to-purple-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Trusted by Industry Leaders</h2>
            <p className="text-xl text-blue-100">Industry-leading verification standards and processes</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {verificationHighlights.map((highlight, index) => (
              <div key={index} className="flex items-center space-x-4 p-6 bg-white bg-opacity-10 rounded-xl backdrop-blur-sm">
                <div className={`w-12 h-12 bg-${highlight.color}-100 rounded-full flex items-center justify-center`}>
                  <highlight.icon className={`h-6 w-6 text-${highlight.color}-600`} />
                </div>
                <span className="text-white font-medium text-lg">{highlight.text}</span>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <button 
              onClick={() => navigate('/verify')}
              className="bg-white text-blue-600 px-8 py-4 rounded-xl font-semibold hover:bg-gray-100 transition-colors inline-flex items-center text-lg shadow-lg"
            >
              Explore Verification Center
              <ArrowRight className="ml-2 h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Testimonials Section */}
      <div className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">What Our Users Say</h2>
            <p className="text-xl text-gray-600">Trusted by thousands of organizations worldwide</p>
          </div>

          <div className="relative">
            <div className="bg-white rounded-2xl shadow-xl p-8 lg:p-12">
              <Quote className="h-12 w-12 text-green-600 mb-6" />
              
              <blockquote className="text-xl lg:text-2xl text-gray-700 leading-relaxed mb-8">
                "{testimonials[currentTestimonial].content}"
              </blockquote>
              
              <div className="flex items-center space-x-4">
                <div className="text-4xl">{testimonials[currentTestimonial].avatar}</div>
                <div>
                  <div className="font-semibold text-gray-900 text-lg">
                    {testimonials[currentTestimonial].name}
                  </div>
                  <div className="text-gray-600">
                    {testimonials[currentTestimonial].role} at {testimonials[currentTestimonial].company}
                  </div>
                  <div className="flex items-center space-x-1 mt-1">
                    {[...Array(testimonials[currentTestimonial].rating)].map((_, i) => (
                      <Star key={i} className="h-4 w-4 text-yellow-400 fill-current" />
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Testimonial indicators */}
            <div className="flex justify-center space-x-2 mt-8">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentTestimonial(index)}
                  className={`w-3 h-3 rounded-full transition-colors ${
                    index === currentTestimonial ? 'bg-green-600' : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Call to Action */}
      <div className="bg-gradient-to-r from-green-600 via-emerald-600 to-blue-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
              Ready to Make an Impact?
            </h2>
            <p className="text-xl lg:text-2xl text-green-100 mb-10 max-w-3xl mx-auto">
              Join thousands of organizations already using Climify to create meaningful environmental change
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <button 
                onClick={() => navigate('/auth?mode=register')}
                className="bg-white text-green-600 px-10 py-4 rounded-xl text-lg font-semibold hover:bg-gray-100 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              >
                Start Your Journey
              </button>
              <button 
                onClick={() => navigate('/marketplace')}
                className="border-2 border-white text-white px-10 py-4 rounded-xl text-lg font-semibold hover:bg-white hover:text-green-600 transition-all flex items-center justify-center"
              >
                <Globe className="mr-2 h-5 w-5" />
                Explore Projects
              </button>
            </div>

            <div className="mt-12 text-green-100 text-center">
              <p className="mb-4">Trusted by leading organizations</p>
              <div className="flex justify-center items-center space-x-8 opacity-60">
                <div className="text-2xl font-bold">GreenTech</div>
                <div className="text-2xl font-bold">EcoSolutions</div>
                <div className="text-2xl font-bold">CleanEnergy Co</div>
                <div className="text-2xl font-bold">SustainableFuture</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer CTA */}
      <div className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div>
              <Clock className="h-12 w-12 text-green-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Quick Setup</h3>
              <p className="text-gray-400">Get started in minutes with our intuitive platform</p>
            </div>
            <div>
              <Shield className="h-12 w-12 text-blue-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Enterprise Security</h3>
              <p className="text-gray-400">Bank-level security for all your transactions</p>
            </div>
            <div>
              <Users className="h-12 w-12 text-purple-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">24/7 Support</h3>
              <p className="text-gray-400">Expert support whenever you need it</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage;